import React from 'react';
import Draggable from '../Draggable/Draggable';
import {
    BarChart, Bar,
    LineChart, Line,
    PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Legend
} from 'recharts';

// FIX-CHART: Chart element wrapper for canvas preview.
// Mirrors TableElement.js pattern: uses Draggable with (id, type, properties) props,
// renders chart body via Recharts inside Draggable. The actual PDF rendering happens
// backend-side in GenerateDoc.cs RenderBarChart. This component is only for design-time
// visual feedback.

export default class ChartElement extends React.Component {

    getChartData(props) {
        const cd = props?.ChartData;
        if (Array.isArray(cd) && cd.length > 0) {
            return cd.map((dp, i) => ({
                label: dp.label ?? dp.Label ?? `Item ${i + 1}`,
                value: Number(dp.value ?? dp.Value ?? 0),
                color: dp.color ?? dp.Color ?? ''
            }));
        }
        // Fallback sample data (Dynamic mode, backend fetches real data at PDF time)
        return [
            {label: 'A', value: 120},
            {label: 'B', value: 180},
            {label: 'C', value: 140},
            {label: 'D', value: 220}
        ];
    }

    renderBarChart(data, w, h, props) {
        const barColor = props?.BarColor || '#3182CE';
        const palette = Array.isArray(props?.ColorPalette) && props.ColorPalette.length > 0
            ? props.ColorPalette
            : [barColor];
        const showGrid = props?.ShowGrid !== false;
        const showValues = props?.ShowValues !== false;
        const showAxisLabels = props?.ShowAxisLabels !== false;

        return (
            <BarChart width={w} height={h} data={data}
                      margin={{top: 10, right: 10, left: 10, bottom: 5}}>
                {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0"/>}
                {showAxisLabels && <XAxis dataKey="label" tick={{fontSize: 10}}/>}
                {showAxisLabels && <YAxis tick={{fontSize: 10}}/>}
                <Bar dataKey="value"
                     label={showValues ? {position: 'top', fontSize: 10, fill: '#333'} : false}>
                    {data.map((entry, idx) => (
                        <Cell key={`cell-${idx}`}
                              fill={entry.color || palette[idx % palette.length]}/>
                    ))}
                </Bar>
            </BarChart>
        );
    }

    renderLineChart(data, w, h, props) {
        const color = props?.BarColor || '#3182CE';
        const showGrid = props?.ShowGrid !== false;
        const showAxisLabels = props?.ShowAxisLabels !== false;

        return (
            <LineChart width={w} height={h} data={data}
                       margin={{top: 10, right: 10, left: 10, bottom: 5}}>
                {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0"/>}
                {showAxisLabels && <XAxis dataKey="label" tick={{fontSize: 10}}/>}
                {showAxisLabels && <YAxis tick={{fontSize: 10}}/>}
                <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2}/>
            </LineChart>
        );
    }

    renderPieChart(data, w, h, props) {
        const barColor = props?.BarColor || '#3182CE';
        const palette = Array.isArray(props?.ColorPalette) && props.ColorPalette.length > 0
            ? props.ColorPalette
            : [barColor, '#E53E3E', '#38A169', '#D69E2E', '#805AD5', '#DD6B20'];
        const showLegend = props?.ShowLegend === true;
        const isDonut = props?.ChartType === 'Donut';
        const outerR = Math.min(w, h) * 0.4;
        const innerR = isDonut ? outerR * 0.5 : 0;

        return (
            <PieChart width={w} height={h}>
                <Pie data={data}
                     dataKey="value"
                     nameKey="label"
                     cx="50%" cy="50%"
                     outerRadius={outerR}
                     innerRadius={innerR}
                     label={props?.ShowValues !== false ? {fontSize: 10, fill: '#333'} : false}>
                    {data.map((entry, idx) => (
                        <Cell key={`pie-${idx}`}
                              fill={entry.color || palette[idx % palette.length]}/>
                    ))}
                </Pie>
                {showLegend && <Legend wrapperStyle={{fontSize: '10px'}}/>}
            </PieChart>
        );
    }

    renderChartContent() {
        const {context} = this.props;
        const properties = context?.properties || {};
        const chartType = properties.ChartType || 'Bar';
        const size = properties.size || {width: 300, height: 200};
        const w = Math.max(Number(size.width) || 300, 80);
        const h = Math.max(Number(size.height) || 200, 60);
        const data = this.getChartData(properties);
        const title = properties.Title || '';

        let chartBody;
        switch (chartType) {
            case 'Line':
                chartBody = this.renderLineChart(data, w, h - (title ? 18 : 0), properties);
                break;
            case 'Pie':
            case 'Donut':
                chartBody = this.renderPieChart(data, w, h - (title ? 18 : 0), properties);
                break;
            case 'Bar':
            default:
                chartBody = this.renderBarChart(data, w, h - (title ? 18 : 0), properties);
                break;
        }

        return (
            <div style={{
                width: '100%',
                height: '100%',
                background: '#fff',
                overflow: 'hidden',
                pointerEvents: 'none'
            }}>
                {title && (
                    <div style={{
                        fontSize: (properties.TitleFontSize || 12) + 'px',
                        textAlign: 'center',
                        height: '18px',
                        fontWeight: 'bold',
                        color: '#333',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis'
                    }}>
                        {title}
                    </div>
                )}
                {chartBody}
            </div>
        );
    }

    render() {
        // Mirror TableElement: Draggable receives id/type/properties explicitly,
        // not context (Default.js uses context, but TableElement doesn't — we follow TableElement).
        const {id, context, onMouseDown, RemoveIconScale, GridSize, OpenProperties} = this.props;
        if (!context) return null;
        const {properties, type} = context;

        return (
            <Draggable
                id={id}
                type={type}
                properties={properties}
                onMouseDown={onMouseDown}
                RemoveIconScale={RemoveIconScale}
                GridSize={GridSize}
                OpenProperties={OpenProperties}
                style={{
                    zIndex: properties.Zindex
                }}
            >
                {this.renderChartContent()}
            </Draggable>
        );
    }
}
